import Modal from 'react-modal';
import classnames from 'classnames';
import { UiMultiply } from '@vyaguta/vyaguta-icons/ui';
import React, { Component, useState } from 'react';

import Loading from '../loading/Loading';

import { ModalButton, ModalCloseHandler, ModalContent, ModalMessage, ModalTitle } from 'interface/modal';

// Make sure to bind modal to your appElement
Modal.setAppElement('#root');

interface CustomModalProps {
  buttons?: ModalButton[];
  title: ModalTitle;
  message?: ModalMessage;
  htmlContent?: ModalContent;
  modalClose?: ModalCloseHandler;
  onClose?: () => void;
  withIcon?: React.ReactNode;
  renderFooter?: boolean;
  onCancelClick: (e?: React.SyntheticEvent) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  subTitle?: ModalTitle;
  headerBorder?: boolean;
  footerBorder?: boolean;
  titlePrefixHtml?: React.ReactNode;
  loading?: boolean;
}

interface CustomModalState {
  isOverFlown: boolean;
  loading: boolean;
}

export class CustomModal extends Component<CustomModalProps, CustomModalState> {
  el?: HTMLDivElement | null;

  constructor(props: CustomModalProps) {
    super(props);
    this.state = {
      isOverFlown: false,
      loading: false,
    };
  }

  isOverFlown = (el?: HTMLDivElement | null) => {
    if (!el) {
      return false;
    }
    return el.scrollHeight > el.clientHeight;
  };

  setLoading(loading: boolean) {
    this.setState({
      loading,
    });
  }

  componentDidMount() {
    this.setState({ isOverFlown: this.isOverFlown(this.el) });
  }

  render() {
    const {
      buttons,
      title,
      message,
      htmlContent,
      modalClose,
      onClose,
      withIcon,
      renderFooter,
      onCancelClick,
      onClick,
      subTitle,
      headerBorder,
      footerBorder,
      titlePrefixHtml,
    } = this.props;
    const { isOverFlown, loading } = this.state;

    return (
      <>
        {withIcon && <div className="lf-modal__status-icon">{withIcon}</div>}
        <div className="lf-modal__content-wrapper" onClick={onClick}>
          <div className={`lf-modal__header ${isOverFlown || headerBorder ? 'lf-modal__header--border' : ''}`}>
            {modalClose && (
              <div className="lf-modal__close-icon">
                <UiMultiply
                  size={20}
                  onClick={() => {
                    if (typeof modalClose === 'function') {
                      modalClose();
                    }
                    if (typeof onClose === 'function') {
                      onClose();
                    }
                  }}
                />
              </div>
            )}

            <div className="lf-modal__title--container">
              <div className="lf-modal__title--inner-container">
                <div
                  className={classnames('lf-modal__title', {
                    [`lf-modal__title--${title.type}`]: title.type,
                  })}
                >
                  <div>{title.text}</div>
                </div>
                <div>{titlePrefixHtml}</div>
              </div>

              {subTitle && <div className={'lf-modal__sub-title'}>{subTitle.text}</div>}
            </div>
          </div>

          {message && (
            <div className="lf-modal__body">
              <p className={classnames('lf-modal__info', { [message.type]: message.type })}>{message.text}</p>
            </div>
          )}

          {htmlContent && (
            <div
              className={`lf-modal__body ${isOverFlown ? 'pt-5x' : ''}`}
              ref={(ref) => {
                this.el = ref;
              }}
            >
              {htmlContent}
            </div>
          )}

          {renderFooter && (
            <div
              className={classnames('lf-modal__footer', { 'lf-modal__footer--border': isOverFlown || footerBorder })}
            >
              {buttons &&
                buttons.map((value, index) => {
                  const btnTheme = value.type === undefined ? 'primary' : value.type;
                  return (
                    <button
                      className={classnames('btn btn--large', `btn--${btnTheme}`, value.className, {
                        'btn--loading': value.showLoading && (this.state.loading || value.isLoading),
                      })}
                      onClick={async () => {
                        this.setLoading(true);
                        if (value.onClick) {
                          await value.onClick();
                        }

                        this.setLoading(false);

                        if (value.close) onCancelClick();
                      }}
                      disabled={value.disabled || loading || value.isLoading}
                      key={index}
                    >
                      <span className="btn__label">{value.text}</span>
                      {value.showLoading && (this.state.loading || value.isLoading) && <Loading />}
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      </>
    );
  }
}

interface Props {
  btnType?: string;
  loading?: boolean;
  btnLabel?: React.ReactNode;
  onClose?: () => void;
  title: ModalTitle;
  TriggerBtn?: React.ComponentType<{ onClick: () => void; interactive: boolean; isLoading?: boolean }>;
  isLoading?: boolean;
  modalStatusIcon?: React.ReactNode;
  buttons?: ModalButton[];
  message?: ModalMessage;
  htmlContent?: ModalContent;
  closeButton?: boolean;
  renderFooter?: boolean;
  customBtnClass?: string;
  disableBtn?: boolean;
  stopPropagation?: boolean;
  interactive?: boolean;
  className?: string;
  subTitle?: ModalTitle;
  headerBorder?: boolean;
  footerBorder?: boolean;
  titlePrefixHtml?: React.ReactNode;
  isOpen?: boolean;
}

const LFModal: React.FC<Props> = (props) => {
  const {
    btnType,
    loading,
    btnLabel,
    onClose,
    title,
    TriggerBtn,
    isLoading,
    modalStatusIcon,
    buttons,
    message,
    htmlContent,
    closeButton,
    renderFooter,
    customBtnClass,
    disableBtn,
    stopPropagation = false,
    interactive = false,
    className,
    subTitle,
    headerBorder = false,
    footerBorder = false,
    titlePrefixHtml,
  } = props;

  const isControlled = props.isOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = isControlled ? props.isOpen : internalIsOpen;

  const showModal = () => {
    if (isControlled) {
      return;
    }

    setInternalIsOpen(true);
  };

  const closeModal = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (stopPropagation) {
      e?.stopPropagation();
    }

    if (onClose) {
      onClose();
    }

    if (!isControlled) {
      setInternalIsOpen(false);
    }
  };

  const handleOnClick = (e: React.SyntheticEvent) => {
    if (stopPropagation) e.stopPropagation();
  };

  const handleButtonOnClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();

    showModal();
  };

  return (
    <>
      {TriggerBtn ? (
        <TriggerBtn onClick={showModal} interactive={interactive} isLoading={isLoading} />
      ) : (
        <button
          className={classnames('btn', { [`btn--${btnType}`]: btnType, [customBtnClass]: customBtnClass })}
          type="button"
          onClick={handleButtonOnClick}
          disabled={disableBtn}
        >
          {btnLabel}
        </button>
      )}
      <Modal
        isOpen={Boolean(isOpen)}
        onRequestClose={closeModal as never}
        className={classnames('lf-modal', { [className]: className })}
        overlayClassName="lf-modal__overlay"
        bodyOpenClassName="overflow-hidden"
      >
        <CustomModal
          title={title}
          loading={loading}
          buttons={buttons}
          message={message}
          onClose={onClose}
          onCancelClick={closeModal}
          htmlContent={htmlContent}
          renderFooter={renderFooter}
          onClick={handleOnClick}
          modalClose={closeButton ? closeModal : ''}
          withIcon={modalStatusIcon ? modalStatusIcon : ''}
          className={className}
          subTitle={subTitle}
          headerBorder={headerBorder}
          footerBorder={footerBorder}
          titlePrefixHtml={titlePrefixHtml}
        />
      </Modal>
    </>
  );
};

export default LFModal;
